import { type IEvent } from 'clients/PlatformApi';
import moment from 'moment';
import { useCallback } from 'react';
import useFluxStore from 'src/hooks/useFluxStore';
import useLocale from 'src/hooks/useLocale';
import { LocationStore } from 'stores/LocationStore';
import SystemReport from 'utils/SystemReport';
import TimeFormatter from 'utils/TimeFormatter';
import { DEFAULT_TIMEZONE } from 'vd-constants';

export interface PrintPanelProps {
  printPanelOpen?: boolean;
  event: IEvent;
  closePanel?: () => void;
}

export function usePrintPanelDialog({
  event,
  closePanel,
}: Pick<PrintPanelProps, 'event' | 'closePanel'>) {
  const locale = useLocale();

  const location = useFluxStore(
    LocationStore,
    (store) => event.siteId && store.getLocation(event.siteId),
  );

  const modalCloseAction = useCallback(() => {
    closePanel?.();
  }, [closePanel]);

  const handlePrintClick = useCallback(() => {
    const SR = new SystemReport().report;
    if (SR.browser.name === 'Safari') {
      document.execCommand('print', false, '');
    } else {
      window.print();
    }
  }, []);

  const { subtitle, title } = event;
  const description = event.details?.description;

  const timezone = location?.timezone || DEFAULT_TIMEZONE;
  const startAndEndAreSame =
    moment.tz(event.startTimeLocal, timezone).startOf('second').valueOf() ===
    moment.tz(event.endTimeLocal, timezone).startOf('second').valueOf();
  const startTimeFormat = TimeFormatter.getString({
    zeroPrefix: false,
    seconds: true,
    showTimezone: startAndEndAreSame,
  });
  const endTimeFormat = TimeFormatter.getString({
    zeroPrefix: false,
    seconds: true,
  });
  const dateFormat = locale.date.format1;
  const date = moment.tz(event.startTimeLocal, timezone).format(dateFormat);
  const startTime = moment.tz(event.startTimeLocal, timezone).format(startTimeFormat);
  const endTime = moment.tz(event.endTimeLocal, timezone).format(endTimeFormat);
  const timeDisplay = startAndEndAreSame ? startTime : `${startTime} - ${endTime}`;

  return {
    locale,
    location,
    modalCloseAction,
    handlePrintClick,
    subtitle,
    title,
    description,
    date,
    timeDisplay,
    timezone: location?.timezone,
  };
}
